// Integration Health Monitoring Hook

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { IntegrationHealth, SystemAlert, AlertStatus } from '@/lib/security/types';

export function useIntegrationHealth(clientId?: string) {
  const queryClient = useQueryClient();

  const { data: healthStatuses, isLoading: healthLoading, error: healthError } = useQuery({
    queryKey: ['integration-health', clientId],
    queryFn: async () => {
      let query = supabase
        .from('integration_health')
        .select('*')
        .order('integration_type');

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as IntegrationHealth[];
    },
  });

  const { data: alerts, isLoading: alertsLoading, error: alertsError } = useQuery({
    queryKey: ['system-alerts', clientId],
    queryFn: async () => {
      let query = supabase
        .from('system_alerts')
        .select('*')
        .in('status', ['open', 'acknowledged'])
        .order('triggered_at', { ascending: false });

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SystemAlert[];
    },
  });

  const updateAlertStatus = useMutation({
    mutationFn: async ({ alertId, status }: { alertId: string; status: AlertStatus }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const updateData: Record<string, unknown> = { status };
      
      if (status === 'acknowledged') {
        updateData.acknowledged_at = new Date().toISOString();
        updateData.acknowledged_by = user?.id;
      } else if (status === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
        updateData.resolved_by = user?.id;
      }

      const { error } = await supabase
        .from('system_alerts')
        .update(updateData)
        .eq('id', alertId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-alerts'] });
    },
  });

  const acknowledgeAlert = (alertId: string) => {
    return updateAlertStatus.mutate({ alertId, status: 'acknowledged' });
  };

  const resolveAlert = (alertId: string) => {
    return updateAlertStatus.mutate({ alertId, status: 'resolved' });
  };

  const snoozeAlert = async (alertId: string, until: Date) => {
    const { error } = await supabase
      .from('system_alerts')
      .update({ 
        status: 'snoozed',
        snoozed_until: until.toISOString(),
      })
      .eq('id', alertId);

    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ['system-alerts'] });
  };

  // Calculate summary stats
  const healthSummary = {
    total: healthStatuses?.length || 0,
    healthy: healthStatuses?.filter(h => h.status === 'healthy').length || 0,
    degraded: healthStatuses?.filter(h => h.status === 'degraded').length || 0,
    down: healthStatuses?.filter(h => h.status === 'down').length || 0,
    unknown: healthStatuses?.filter(h => h.status === 'unknown').length || 0,
  };

  const alertSummary = {
    total: alerts?.length || 0,
    critical: alerts?.filter(a => a.severity === 'critical').length || 0,
    warning: alerts?.filter(a => a.severity === 'warning').length || 0,
    info: alerts?.filter(a => a.severity === 'info').length || 0,
    open: alerts?.filter(a => a.status === 'open').length || 0,
    acknowledged: alerts?.filter(a => a.status === 'acknowledged').length || 0,
  };

  return {
    healthStatuses: healthStatuses || [],
    alerts: alerts || [],
    healthSummary,
    alertSummary,
    isLoading: healthLoading || alertsLoading,
    error: healthError || alertsError,
    acknowledgeAlert,
    resolveAlert,
    snoozeAlert,
    isUpdating: updateAlertStatus.isPending,
  };
}

/**
 * Create or update integration health status
 */
export async function updateIntegrationHealth(
  integrationType: string,
  integrationName: string,
  status: IntegrationHealth['status'],
  details?: {
    clientId?: string;
    errorMessage?: string;
    responseMs?: number;
  }
) {
  const now = new Date().toISOString();

  // Try to find existing record
  const { data: existing } = await supabase
    .from('integration_health')
    .select('id, failure_count, success_count')
    .eq('integration_type', integrationType)
    .eq('integration_name', integrationName)
    .eq('client_id', details?.clientId || null)
    .maybeSingle();

  const isSuccess = status === 'healthy';
  
  const healthData = {
    integration_type: integrationType,
    integration_name: integrationName,
    client_id: details?.clientId,
    status,
    last_check_at: now,
    last_success_at: isSuccess ? now : undefined,
    last_failure_at: !isSuccess ? now : undefined,
    failure_count: isSuccess ? 0 : (existing?.failure_count || 0) + 1,
    success_count: isSuccess ? (existing?.success_count || 0) + 1 : existing?.success_count || 0,
    avg_response_ms: details?.responseMs,
    error_message: details?.errorMessage || null,
  };

  if (existing) {
    await supabase
      .from('integration_health')
      .update(healthData)
      .eq('id', existing.id);
  } else {
    await supabase
      .from('integration_health')
      .insert(healthData);
  }
}

/**
 * Create a system alert
 */
export async function createSystemAlert(
  alertType: string,
  title: string,
  severity: 'info' | 'warning' | 'critical',
  details?: {
    clientId?: string;
    description?: string;
    resourceType?: string;
    resourceId?: string;
    metadata?: Record<string, unknown>;
  }
) {
  const insertData = {
    alert_type: alertType,
    title,
    severity,
    status: 'open' as const,
    client_id: details?.clientId,
    description: details?.description,
    resource_type: details?.resourceType,
    resource_id: details?.resourceId,
    metadata: details?.metadata ? JSON.parse(JSON.stringify(details.metadata)) : {},
  };

  const { error } = await supabase
    .from('system_alerts')
    .insert(insertData);

  if (error) {
    console.error('Failed to create alert:', error);
    throw error;
  }
}
